import { Outcome } from "@ethossoftworks/outcome"
import { Bloc } from "@lib/bloc/Bloc"
import { Attachment } from "@model/Attachment"
import { HistoryItem } from "@model/HistoryItem"
import { ServiceItemForm, newServiceItemForm } from "@model/serviceRequests/ServiceItemForm"
import { Step } from "@model/Step"
import { CompletableTaskValue, InspectionTaskValue, Task } from "@model/Task"
import { WorkRequestStatus, WorkRequestType } from "@model/workRequests/WorkRequest"
import { WorkRequestService } from "@service/workRequests/WorkRequestService"
import { UserBloc } from "@state/user/UserBloc"
import { v4 as uuidv4 } from "uuid"
export class ServiceItemBloc extends Bloc<ServiceItemForm> {
    constructor(initialId: number, private workRequestService: WorkRequestService, private userBlock: UserBloc) {
        super(newServiceItemForm(initialId), { persistStateOnDispose: false })
    }

    private getFilesId(): string {
        let { filesId } = this.state
        if (!filesId) {
            filesId = uuidv4()
            this.update({ ...this.state, filesId })
        }
        return filesId
    }

    setState = async (newState: ServiceItemForm) => {
        if (
            newState.workRequestType == WorkRequestType.Preventative ||
            newState.workRequestType == WorkRequestType.Inspection
        ) {
            if (newState.step) {
                this.update({ ...this.state, isLoading: true })
                let tasks = await this.workRequestService.fetchServiceScheduleStepTasks(
                    newState.step!.id,
                    newState.workRequestType
                )

                //if (!newState.workOrderId) {
                //     // Initialize every task with NA
                //     tasks = tasks.map((t: Task) => {
                //         if (newState.workRequestType === WorkRequestType.Inspection) {
                //             return { ...t, value: InspectionTaskValue.NA }
                //         }
                //         return { ...t, value: CompletableTaskValue.NA }
                //     })
                //     newState.tasks = tasks
                //}

                this.update({ ...this.state, isLoading: false })
            }
        }

        if (newState.filesId) {
            const atachmentsResponse = await this.workRequestService.fetchAttachments(newState.filesId!)
            if (atachmentsResponse.isOk()) newState.attachments = atachmentsResponse.value
        }

        this.update(newState)
    }

    stepChanged = async (value: number, steps: Step[]) => {
        this.update({ ...this.state, areTasksLoading: true })
        const step = steps.find((item) => item.id === value)
        if (!step) return
        this.updateField("step", step)

        if (step.id === this.state.initialStep?.id) {
            this.updateField("tasks", this.state.initialTasks)
            this.updateField("name", this.generatePMInspectionName(step))
        } else {
            // When WR is not added to a WO, it should get the tasks from the service schedule step
            let tasks = await this.workRequestService.fetchServiceScheduleStepTasks(step.id, this.state.workRequestType)
            tasks = tasks.map(this.mapPMInspectionTasksToNA)

            this.updateField("name", this.generatePMInspectionName(step))
            this.updateField("tasks", tasks)
        }

        this.update({ ...this.state, areTasksLoading: false })
    }

    mapPMInspectionTasksToNA = (t: Task) => {
        if (this.state.workRequestType === WorkRequestType.Inspection) {
            return { ...t, value: InspectionTaskValue.NA }
        }
        return { ...t, value: CompletableTaskValue.NA }
    }

    generatePMInspectionName = (step: Step) => {
        return step?.label + ` (Added to ${this.state.schedule?.name})`
    }

    /** Generic field update */
    updateField = <K extends keyof ServiceItemForm>(field: K, value: ServiceItemForm[K]) => {
        this.update({ ...this.state, [field]: value })
    }

    private updateAttachments = (attachments: Attachment[]) => {
        this.update({ ...this.state, attachments })
    }

    /** Upload new attachments */
    attachmentsChanged = async (files: File[], formData: FormData, userId: number, companyId: number) => {
        const fileId: string = this.getFilesId()
        this.update({ ...this.state, isSaving: true })

        const response = await this.workRequestService.uploadAttachments(fileId, Array.from(files), userId, companyId)

        if (response.isOk()) {
            this.updateAttachments(this.state.attachments.concat(response.value))
        }

        this.update({ ...this.state, isSaving: false })
        return response
    }

    /** Delete one attachment */
    attachmentsDelete = (attachmentName: string, userId: number, companyId: number) => {
        const filesId: string = this.getFilesId()
        this.effect({
            id: this.attachmentsDelete,
            block: async (job) => {
                if (!attachmentName || !filesId) return Outcome.error(undefined)
                this.update({ ...this.state, isSaving: true })
                const response = await job.pause(this.workRequestService.deleteAttachments(filesId, [attachmentName]))
                if (response.isOk()) {
                    this.updateAttachments(this.state.attachments.filter((i) => i.name !== attachmentName))
                }
                return response
            },
            onDone: () => this.update({ ...this.state, isSaving: false }),
        })
    }

    /** Clear all attachments (on cancel or remove item) */
    clearAttachmentsOnCancel = () => {
        if (this.state.filesId && this.state.attachments.length > 0) {
            this.clearAttachments(
                this.state.filesId,
                this.state.attachments.map((i) => i.name)
            )
        }
    }

    deleteItem = async () => {
        this.update({ ...this.state, areTasksLoading: true })
        if (this.state.id && this.state.workOrderId) {
            let result = await this.workRequestService.dismissWorkRequest(
                this.state.id,
                "DELETED ITEM IN M2.5",
                this.userBlock.state.user.id
            )
        }

        this.update({ ...this.state, areTasksLoading: false })
    }

    private clearAttachments = (id: string, fileNames: string[]) => {
        this.effect({
            id: this.clearAttachments,
            block: async (job) => {
                this.update({ ...this.state, isSaving: true })
                const response = await job.pause(this.workRequestService.deleteAttachments(id, fileNames))
                if (response.isOk()) {
                    this.updateAttachments([])
                }
                return response
            },
            onDone: () => this.update({ ...this.state, isSaving: false }),
        })
    }

    fetchHistory = (workRequestId: number) => {
        this.effect({
            id: this.fetchHistory,
            block: async (job) => {
                this.update({ ...this.state, historyIsLoading: true })
                const outcome = await job.pause(this.workRequestService.fetchWorkRequestHistory(workRequestId))
                if (outcome.isError()) {
                    this.update({ ...this.state, historyIsLoading: false })
                    return Outcome.error(undefined)
                }
                this.update({ ...this.state, history: outcome.value, historyIsLoading: false })
                return Outcome.ok(undefined)
            },
        })
    }

    rotateImage = (attachmentName: string, angle: number, userId: number, companyId: number) => {
        const fileId: string = this.getFilesId()
        this.effect({
            id: this.rotateImage,
            block: async (job) => {
                if (!attachmentName || !fileId) return Outcome.error(undefined)

                // Call WorkRequestService.rotateImage
                const response = await job.pause(this.workRequestService.rotateImage(fileId, attachmentName, angle))

                if (response.isOk()) {
                    // Clone current attachments
                    const attachments = JSON.parse(JSON.stringify(this.state.attachments)) as Attachment[]

                    // Find the one we rotated
                    const modified = attachments.find(
                        (x) => x.name.toLowerCase().trim() === attachmentName.toLowerCase().trim()
                    )

                    if (modified) {
                        // Force cache-busting param so UI reloads the rotated image
                        modified.url = modified.url.split("&rotated=")[0] + "&rotated=" + new Date().getTime()

                        // Update state with modified attachments
                        this.update({
                            ...this.state,
                            attachments: attachments,
                        })
                    }
                }

                return response
            },
        })
    }
}
